import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import moment from 'moment';
import { combineLatest, merge, Observable, ReplaySubject } from 'rxjs';
import { filter, map, startWith, take, takeWhile, tap } from 'rxjs/operators';
import { IColumn } from '../../../common';
import * as fromDataServices from '../../../data-services';
import * as fromLookups from '../../../lookups/store';
import * as fromShared from '../../../shared';
import { flatDeep, getDynamicColumns } from '../../../shared';
import { LoggingService } from '../../../shared/services/logging';
import * as fromUserInfo from '../../../user-info';
@Component({
    selector: 'ef-indirect-rate-history-container',
    templateUrl: './indirect-rate-history-container.component.html',
    styleUrls: ['./indirect-rate-history-container.component.scss'],
    standalone: false
})
export class IndirectRateHistoryContainerComponent
    implements OnInit, OnDestroy
{
    IndirectRateHistoryModelDS: EntityCollectionService<fromDataServices.IndirectRateHistory.IndirectRateHistoryModel>;
    IndirectRateHistoryModel$: Observable<
        fromDataServices.IndirectRateHistory.IndirectRateHistoryModel[]
    >;
    fiscalYears$: Observable<fromShared.SelectItem[]>;
    RateChangeOnlySubject = new ReplaySubject<boolean>(1);
    colGroups: IColumn[] = [];
    cols: IColumn[] = [];
    filterFormGroup: UntypedFormGroup;
    process$: Observable<fromShared.Process>;
    private active = true;
    private userInfo$: Observable<fromUserInfo.IUserInfo>;
    userInfo: fromUserInfo.IUserInfo;
    constructor(
        private log: LoggingService,
        public entityServices: EntityServices,
        private store: Store
    ) {
        this.log.verbose({ type: 'IndirectRateHistoryContainerComponent::' });
        this.IndirectRateHistoryModelDS =
            entityServices.getEntityCollectionService<fromDataServices.IndirectRateHistory.IndirectRateHistoryModel>(
                fromDataServices.IndirectRateHistory.entityName
            );

        this.filterFormGroup = new UntypedFormGroup({
            FiscalYear: new UntypedFormControl(null),
            startdate: new UntypedFormControl(null),
            enddate: new UntypedFormControl(null)
        });
    }
    ngOnInit(): void {
        this.userInfo$ = this.store.pipe(select(fromUserInfo.getUserInfo));
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));

        this.IndirectRateHistoryModel$ =
            this.IndirectRateHistoryModelDS.entities$.pipe(
                map(items => [...clone(items)])
            );

        this.userInfo$.pipe(takeWhile(() => this.active)).subscribe(userInfo => {
            this.userInfo = userInfo;
        });

        const FYChanged$ = merge(
            this.userInfo$.pipe(map(x => x.FYEndDate)),
            this.filterFormGroup.controls['FiscalYear'].valueChanges
        );
        const StartDateChanged$ = merge(
            this.userInfo$.pipe(map(x => x.FYStartDate)),
            this.filterFormGroup.controls['startdate'].valueChanges
        );
        const EndDateChanged$ = merge(
            this.userInfo$.pipe(map(x => x.FYEndDate)),
            this.filterFormGroup.controls['enddate'].valueChanges
        );

        if (this.userInfo != null) {
            this.filterFormGroup.controls['startdate'].setValue(this.userInfo.FYStartDate);
            this.filterFormGroup.controls['enddate'].setValue(this.userInfo.FYEndDate);
        }

        // combineLatest([
        //     this.RateChangeOnlySubject.pipe(startWith(false)),
        //     FYChanged$
        // ])
        //     .pipe(takeWhile(() => this.active))
        //     .subscribe(([Ratechangeonly, FYEndDate]) => {
        //         this.IndirectRateHistoryModelDS.clearCache();
        //         this.IndirectRateHistoryModelDS.getWithQuery({
        //             FYEndDate: moment(FYEndDate).format('YYYY-MM-DD'),
        //             Ratechangeonly: Ratechangeonly as any
        //         });
        //     });
        combineLatest([
            this.RateChangeOnlySubject.pipe(startWith(false)),
            StartDateChanged$,
            EndDateChanged$
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(([Ratechangeonly, StartDate, EndDate]) => {
                this.IndirectRateHistoryModelDS.clearCache();
                if (moment(StartDate).isValid() && moment(EndDate).isValid()) {
                    if (new Date(StartDate).getTime() < new Date(EndDate).getTime()) {
                        this.IndirectRateHistoryModelDS.getWithQuery({
                            StartDate: moment(StartDate).format('YYYY-MM-DD'),
                            FYEndDate: moment(EndDate).format('YYYY-MM-DD'),
                            Ratechangeonly: Ratechangeonly as any
                        });
                    }
                }
            });
        this.IndirectRateHistoryModel$.pipe(
            takeWhile(() => this.active),
            filter(items => items && items.length > 0),
            tap(items =>
                this.log.verbose({
                    type: 'IndirectRateHistoryContainerComponent:IndirectRateHistoryModel$',
                    items
                })
            )
        ).subscribe(items => this.mapColumns(items[0]));
        this.fiscalYears$ = this.store.pipe(
            select(
                fromLookups.ScheduledDateSelectors.selectScheduledDateType({
                    ScheduledDateType: 'FYE'
                })
            ),
            filter(dates => !!dates && dates.length > 0),
            take(1),
            map(items =>
                items.map(item => ({
                    label: moment(item.ScheduledEndDate).format('MM/DD/YYYY'),
                    value: moment(item.ScheduledEndDate).format('MM/DD/YYYY')
                }))
            )
        );
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    getColSpan(item, column) {
        // let keys = Object.keys(item[column]);
        const cols = Object.keys(item[column]);
        this.log.verbose({
            type: 'IndirectRateHistoryContainerComponent:getColSpan',
            cols,
            column,
            item
        });
        return cols.length;
    }

    mapColumns(
        item: fromDataServices.IndirectRateHistory.IndirectRateHistoryModel
    ) {
        // getBudgetDynamicColumns returns the dynamic column groups, i.e 01January or Week Ending XXX
        // each of these groups has data nested under it, i.e. hours, amounts, variance, etc
        const filterItems = ['Status', 'Row_Version', 'updates'];

        const dynCols = getDynamicColumns(item, filterItems);

        this.colGroups = dynCols.map(x => ({
            field: x,
            header: x,
            colspan: this.getColSpan(item, x), // apparently width 0 TD's don't count, also remove TxnID
            showColumn: true,
            group: null
        }));

        // this takes the column groups and sets up the columns under each group with an IColumn structure
        this.cols = flatDeep(
            this.colGroups.map(cg =>
                Object.keys(item[cg.field])
                    .map(x => ({
                        group: cg.field,
                        field: x,
                        header: x,
                        exportable: x === 'TxnID' ? false : true,
                        style:
                            x === 'TxnID'
                                ? 'width:0;display:none;'
                                : 'width: 8em;',
                        // style: 'width: 7em',
                        class:
                            x === 'Amount'
                                ? 'currency'
                                : x === 'Hours'
                                ? 'hours'
                                : `disabled ${
                                      x.includes('Amount') ? 'currency' : ''
                                  }`,
                        // class: 'currency',
                        showColumn: true
                    }))
                    .filter(x => x.showColumn)
            )
        );
        this.log.verbose({
            type: 'IndirectRateHistoryContainerComponent:mapColumns',
            cols: this.cols,
            colgroups: this.colGroups,
            item
        });
    }
}
